<template>
  <validation-observer ref="appliedDiscountForm" v-slot="{ invalid }">
    <lenon-modal title="Apply Fee Discount" :show="modalOpened" :show-overlay="loading" size="md"
      @onClose="closeAppliedDiscountModal()" @onOk="applyDiscount()" :ok-disabled="invalid || !selectedTarget"
      :loading="loading">
      <error-display :error="error" />

      <lenon-select v-model="selectedDiscountId" name="fee_discount_id" :options="feeDiscounts"
        label-name="title" value-name="id" label="Fee Discount" placeholder="Select a discount to apply"
        rules="required" @input="handleDiscountChange" />

      <div v-if="selectedDiscount" class="mb-3">
        <b-card>
          <b-card-header>
            <h6 class="mb-0">
              <feather-icon icon="TagIcon" size="16" class="mr-2" />
              Discount Details
            </h6>
          </b-card-header>
          <b-card-body>
            <p class="mb-2">
              <strong>Fee Setting:</strong> {{ selectedDiscount.fee_setting.title }}
            </p>
            <p class="mb-2">
              <strong>Original Amount:</strong> 
              <b-badge variant="success" class="ml-1">
                {{ $store.getters['auth/currency'] }} {{ selectedDiscount.fee_setting.amount }}
              </b-badge>
            </p>
            <p class="mb-2">
              <strong>Discount Type:</strong> 
              <b-badge :variant="selectedDiscount.discount_type === 'percentage' ? 'success' : 'warning'" class="ml-1">
                {{ selectedDiscount.discount_type === 'percentage' ? 'Percentage' : 'Fixed Amount' }}
              </b-badge>
            </p>
            <p class="mb-0">
              <strong>Discount Value:</strong> 
              <b-badge variant="danger" class="ml-1">
                {{ selectedDiscount.discount_type === 'percentage' ? selectedDiscount.discount + '%' :
                  $store.getters['auth/currency'] + ' ' + selectedDiscount.discount }}
              </b-badge>
            </p>
          </b-card-body>
        </b-card>
      </div>

      <lenon-select v-model="applicationType" name="application_type" :options="applicationTypes" label-name="label"
        value-name="value" label="Apply To" placeholder="Choose application method" rules="required"
        @input="handleApplicationTypeChange" />

      <!-- Search Section -->
      <div v-if="applicationType" class="mb-3">
        <label class="form-label">
          Search {{ applicationType === 'student' ? 'Students' : 'Parents' }}
          <b class="text-danger" style="font-size: 15px">*</b>
        </label>
        <div class="row">
          <div class="col-md-12">
            <lenon-input v-model="searchTerm" name="search_term"
              :placeholder="`Search ${applicationType === 'student' ? 'students by name or ID' : 'parents by name or phone'}`"
              suffix-icon="SearchIcon"  @onEnterPressed="performSearch" @onSuffixIconClick="performSearch" />
          </div>
        </div>
      </div>

      <!-- Search Results -->
      <div v-if="searchResults.length > 0" class="mb-3">
        <label class="form-label">
          <feather-icon icon="SearchIcon" size="16" class="mr-1" />
          Search Results ({{ searchResults.length }})
        </label>
        <div style="max-height: 350px; overflow-y: auto;">
          <b-list-group>
            <student-card
              v-for="result in searchResults.filter(r => r.type === 'student')"
              :key="result.id"
              :student="result"
              :is-selected="selectedStudents.some(s => s.id === result.id)"
              @select="selectTarget"
            />
            <parent-card
              v-for="result in searchResults.filter(r => r.type === 'parent')"
              :key="result.id"
              :parent="result"
              :is-selected="selectedTarget && selectedTarget.id === result.id"
              @select="selectTarget"
              @children-changed="handleChildrenChanged"
            />
          </b-list-group>
        </div>
      </div>
      <!-- Application Summary -->
      <div v-if="selectedStudents.length > 0 && selectedDiscount" class="mb-3">
        <b-alert show variant="success" class="p-1">
          <strong>Summary:</strong>
          <span v-if="applicationType === 'student'">
            The discount "{{ selectedDiscount.title }}" will be applied to {{ selectedStudentsCount }} student(s).
          </span>
          <span v-else>
            The discount "{{ selectedDiscount.title }}" will be applied to {{ selectedChildrenCount }} 
            of {{ selectedTarget.children.length }} children of {{ selectedTarget.full_name }}.
          </span>
        </b-alert>
      </div>

      <template #modal-actions>
        <b-row class="float-right">
          <lenon-button variant="flat-danger" icon="XIcon" class="mr-1" label="Cancel"
            @onClick="closeAppliedDiscountModal" />
          <lenon-button 
            label="Apply Discount" 
            :disabled="invalid || selectedStudents.length === 0" 
            :loading="loading"
            @onClick="applyDiscount()" />
        </b-row>
      </template>
    </lenon-modal>
  </validation-observer>
</template>

<script>
import LenonModal from '@/lenon/components/LenonModal.vue'
import LenonButton from '@/lenon/components/LenonButton.vue'
import LenonInput from '@/lenon/components/LenonInput.vue'
import LenonSelect from '@/lenon/components/LenonSelect.vue'
import ErrorDisplay from '@/lenon/components/ErrorDisplay.vue'
import StudentCard from './StudentCard.vue'
import ParentCard from './ParentCard.vue'
import showToast from '@/lenon/mixins/showToast'
import {
  BRow, BCol, BCard, BCardHeader, BCardBody, BBadge, BAvatar, BAlert, BListGroup, BListGroupItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import logData from '@/libs/log'

export default {
  name: 'AppliedDiscountSetup',
  components: {
    LenonModal,
    LenonButton,
    LenonInput,
    LenonSelect,
    ErrorDisplay,
    StudentCard,
    ParentCard,
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BBadge,
    BAvatar,
    BAlert,
    BListGroup,
    BListGroupItem,
    ValidationObserver,
  },
  mixins: [showToast],
  props: {
    modalOpened: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      searching: false,
      error: {},
      selectedDiscountId: null,
      applicationType: null,
      searchTerm: '',
      selectedTarget: null,
      selectedStudents: [], // Array of selected student objects with {id, class_id} for discount application
      selectedChildren: [], // Array of selected child objects with {id, class_id} for parent applications
      applicationTypes: [
        { label: 'Single Student', value: 'student' },
        { label: 'All Children of Parent', value: 'parent' },
      ],
    }
  },
  computed: {
    feeDiscounts() {
      return this.$store.getters['feeDiscounts/feeDiscounts']
    },
    selectedDiscount() {
      if (!this.selectedDiscountId) return null
      return this.feeDiscounts.find(d => d.id === this.selectedDiscountId)
    },
    searchResults() {
      return this.$store.getters['feeDiscounts/searchResults']
    },
    selectedChildrenCount() {
      return this.selectedChildren.length
    },
    selectedStudentsCount() {
      return this.selectedStudents.length
    },
  },
  watch: {
    modalOpened(val) {
      if (val) {
        this.fetchFeeDiscounts()
      } else {
        this.resetForm()
      }
    },
  },
  methods: {
    resetForm() {
      this.selectedDiscountId = null
      this.applicationType = null
      this.searchTerm = ''
      this.selectedTarget = null
      this.selectedStudents = []
      this.selectedChildren = []
      this.error = {}
      this.$store.commit('feeDiscounts/setSearchResults', [])
      this.$refs.appliedDiscountForm.reset()
    },
    handleDiscountChange() {
      // Reset selection when discount changes
      this.selectedTarget = null
      this.selectedStudents = []
      this.selectedChildren = []
      this.$store.commit('feeDiscounts/setSearchResults', [])
    },
    handleApplicationTypeChange() {
      // Reset selection when type changes
      this.selectedTarget = null
      this.selectedStudents = []
      this.selectedChildren = []
      this.searchTerm = ''
      this.$store.commit('feeDiscounts/setSearchResults', [])
    },
    selectTarget(target) {
      if (target.type === 'student') {
        // Toggle logic for students
        const studentIndex = this.selectedStudents.findIndex(s => s.id === target.id)
        if (studentIndex !== -1) {
          // If student is already selected, deselect them
          this.selectedStudents.splice(studentIndex, 1)
          // If no students selected, clear selectedTarget
          if (this.selectedStudents.length === 0) {
            this.selectedTarget = null
          }
        } else {
          // If student is not selected, add them to the selection
          this.selectedStudents.push({ id: target.id, class_id: target.class_id })
          this.selectedTarget = target // Keep for UI display purposes
        }
      } else {
        // For parents, just select (no toggle needed as children handle individual toggling)
        this.selectedTarget = target
        // Initialize selectedChildren with all children IDs when parent is selected
        if (target && target.type === 'parent' && target.children) {
          this.selectedChildren = target.children.map(child => ({ id: child.id, class_id: child.class_id }))
          // Also sync selectedStudents for parents
          this.selectedStudents = [...this.selectedChildren]
        }
      }
    },
    handleChildrenChanged(selectedChildrenObjects) {
      this.selectedChildren = selectedChildrenObjects
      // For parents, sync selectedStudents with selectedChildren
      if (this.applicationType === 'parent') {
        this.selectedStudents = [...selectedChildrenObjects]
      }
    },
    performSearch() {
      if (!this.searchTerm.trim()) {
        this.showInfo('Please enter a search term')
        return
      }

      if (!this.applicationType) {
        this.showInfo('Please select application type first')
        return
      }

      this.searching = true
      this.$http.get('search', {
        params: {
          search_type: this.applicationType,
          search_term: this.searchTerm.trim(),
          limit: 20
        }
      })
        .then(res => {
          const results = res.data.results || []
          this.$store.commit('feeDiscounts/setSearchResults', results)
          if (results.length === 0) {
            this.showInfo(`No ${this.applicationType === 'student' ? 'students' : 'parents'} found matching your search`)
          }
        })
        .catch(err => {
          logData(err)
          this.showError('Failed to search')
          this.$store.commit('feeDiscounts/setSearchResults', [])
        })
        .finally(() => {
          this.searching = false
        })
    },
    closeAppliedDiscountModal() {
      this.$emit('modalClosed')
    },
    applyDiscount() {
      if (!this.selectedDiscountId) {
        this.showError('Please select a discount')
        return
      }

      if (this.selectedStudents.length === 0) {
        this.showError('Please select at least one student to apply the discount to')
        return
      }

      this.loading = true
      this.error = {}

      // Construct the payload structure as requested
      const payload = {
        students: this.selectedStudents.map(s => ({ id: s.id, class_id: s.class_id })),
        fee_discount_id: this.selectedDiscountId
      }

      console.log('Applying discount with payload:', payload)

      this.$http.post('applied-discounts', payload)
        .then(res => {
          const appliedDiscounts = res.data || []
          
          if (Array.isArray(appliedDiscounts)) {
            this.$store.commit('feeDiscounts/addMultipleAppliedDiscounts', appliedDiscounts)
          } else {
            this.$store.commit('feeDiscounts/addAppliedDiscount', appliedDiscounts)
          }

          const count = this.selectedStudents.length
          this.showSuccess(`Discount applied successfully to ${count} student(s)`)
          this.$emit('discountApplied')
          this.closeAppliedDiscountModal()
        })
        .catch(err => {
          this.error = err.response?.data || { message: 'Failed to apply discount' }
          logData(err)
          this.showError('Failed to apply discount')
        })
        .finally(() => {
          this.loading = false
        })
    },
    fetchFeeDiscounts() {
      this.$http.get('fee-discounts')
        .then(res => {
          this.$store.commit('feeDiscounts/setFeeDiscounts', res.data)
        })
        .catch(err => {
          logData(err)
          this.showError('Failed to load discounts')
        })
    },
  },
}
</script>

<style scoped>
/* Minimal custom styling for dark mode compatibility */
</style>
